import React, { useState } from 'react';
import styles from './styles.module.scss';
import { gql } from '../../utils/graphql';

interface LoginProps {
	onLoginSuccess: (token: string, ambassador: any) => void;
}

const Login: React.FC<LoginProps> = ({ onLoginSuccess }) => {
	const [isRegistering, setIsRegistering] = useState(false);
	const [isForgotPassword, setIsForgotPassword] = useState(false);
	const [step, setStep] = useState(1); // 1 = email/password, 2 = personal details

	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');

	// Registration specific fields (step 2)
	const [firstName, setFirstName] = useState('');
	const [lastName, setLastName] = useState('');
	const [telephone, setTelephone] = useState('');
	const [instagram, setInstagram] = useState('');
	const [tiktok, setTiktok] = useState('');

	const [error, setError] = useState('');
	const [loading, setLoading] = useState(false);
	const [forgotEmail, setForgotEmail] = useState('');
	const [forgotSuccess, setForgotSuccess] = useState('');

	const handleNextStep = (e: React.FormEvent) => {
		e.preventDefault();
		setError('');

		if (!email || !password) {
			setError('Te rugăm să introduci adresa de email și parola');
			return;
		}
		if (password.length < 6) {
			setError('Parola trebuie să aibă cel puțin 6 caractere');
			return;
		}

		setStep(2);
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError('');
		setLoading(true);

		try {
			if (isRegistering) {
				if (!firstName || !lastName || !telephone) {
					setError('Completează toate câmpurile obligatorii (Nume, Prenume, Telefon).');
					setLoading(false);
					return;
				}

				const query = `
					mutation RegisterAmbassador($input: RegisterAmbassadorInput!) {
						registerAmbassador(input: $input) {
							token
							ambassador {
								id
								firstName
								lastName
								email
								telephone
								events {
									id
									name
									city
									date
									inviteId
								}
								instagram
								tiktok
								points
								spentPoints
								soldTickets
								discountCodes {
									code
									discountId
									event {
										id
										name
									}
								}
								profileImage
								redeemedPrizes {
									name
									cost
									date
									isValidated
								}
							}
						}
					}
				`;

				const response = await gql(query, {
					input: {
						email, password, firstName, lastName, telephone, instagram, tiktok
					}
				});

				if (response.data?.errors) {
					throw new Error(response.data.errors[0].message);
				}

				const { token, ambassador } = response.data.data.registerAmbassador;
				onLoginSuccess(token, ambassador);

			} else {
				const query = `
					mutation AmbassadorLogin($email: String!, $password: String!) {
						ambassadorLogin(input: { email: $email, password: $password }) {
							token
							ambassador {
								id
								firstName
								lastName
								email
								telephone
								events {
									id
									name
									city
									date
									inviteId
								}
								instagram
								tiktok
								points
								spentPoints
								soldTickets
								discountCodes {
									code
									discountId
									event {
										id
										name
									}
								}
								profileImage
								redeemedPrizes {
									name
									cost
									date
									isValidated
								}
							}
						}
					}
				`;

				const response = await gql(query, { email, password });

				if (response.data?.errors) {
					throw new Error(response.data.errors[0].message);
				}

				const { token, ambassador } = response.data.data.ambassadorLogin;
				onLoginSuccess(token, ambassador);
			}
		} catch (err: any) {
			console.error(err);
			setError(err.message || (isRegistering ? 'Înregistrarea a eșuat' : 'Autentificarea a eșuat'));
		} finally {
			setLoading(false);
		}
	};

	const toggleMode = () => {
		setIsRegistering(!isRegistering);
		setIsForgotPassword(false);
		setStep(1);
		setError('');
		setForgotSuccess('');
	};

	const handleForgotPassword = async (e: React.FormEvent) => {
		e.preventDefault();
		setError('');
		setForgotSuccess('');
		setLoading(true);

		try {
			const query = `
				mutation RequestAmbassadorPasswordReset($email: String!) {
					requestAmbassadorPasswordReset(email: $email)
				}
			`;

			const response = await gql(query, { email: forgotEmail });

			if (response.data?.errors) {
				throw new Error(response.data.errors[0].message);
			}

			setForgotSuccess('Dacă adresa de email există în sistem, vei primi un link de resetare.');
		} catch (err: any) {
			setError(err.message || 'A apărut o eroare');
		} finally {
			setLoading(false);
		}
	};

	const getTitle = () => {
		if (isForgotPassword) return 'Resetare Parolă';
		if (!isRegistering) return 'Autentificare Ambasador';
		return step === 1 ? 'Creare Cont' : 'Detaliile Tale';
	};

	const getSubtitle = () => {
		if (isForgotPassword) return 'Introdu adresa ta de email';
		if (!isRegistering) return 'Bine ai revenit la Maneliada';
		return step === 1 ? 'Începe cu adresa de email și parola' : 'Spune-ne câte ceva despre tine';
	};

	return (
		<div className={styles.authContainer}>
			<h1 className={styles.title}>{getTitle()}</h1>
			<p className={styles.userHandle} style={{ marginBottom: '2rem' }}>
				{getSubtitle()}
			</p>

			{/* Step indicator for registration */}
			{isRegistering && (
				<div className={styles.stepIndicator}>
					<div className={`${styles.stepDot} ${step >= 1 ? styles.stepActive : ''}`}>1</div>
					<div className={styles.stepLine}>
						<div className={`${styles.stepLineFill} ${step >= 2 ? styles.stepLineFilled : ''}`} />
					</div>
					<div className={`${styles.stepDot} ${step >= 2 ? styles.stepActive : ''}`}>2</div>
				</div>
			)}

			{/* FORGOT PASSWORD */}
			{isForgotPassword && (
				<div className={styles.card}>
					{forgotSuccess ? (
						<>
							<div className={`${styles.msgBox} ${styles.msgSuccess}`}>
								{forgotSuccess}
							</div>
							<button
								type="button"
								onClick={() => { setIsForgotPassword(false); setForgotSuccess(''); setError(''); }}
								className={styles.button}
								style={{ width: '100%' }}
							>
								Înapoi la Autentificare
							</button>
						</>
					) : (
						<form onSubmit={handleForgotPassword}>
							<input
								type="email"
								placeholder="Adresă de Email *"
								className={styles.inputBase}
								value={forgotEmail}
								onChange={(e) => setForgotEmail(e.target.value)}
								required
								disabled={loading}
							/>

							{error && (
								<div className={`${styles.msgBox} ${styles.msgError}`}>
									{error}
								</div>
							)}

							<button type="submit" className={styles.button} disabled={loading} style={{ width: '100%' }}>
								{loading ? 'Se trimite...' : 'Trimite Link de Resetare'}
							</button>

							<div style={{ marginTop: '1.5rem', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '1.5rem' }}>
								<button
									type="button"
									onClick={() => { setIsForgotPassword(false); setError(''); }}
									style={{
										background: 'transparent',
										border: 'none',
										color: '#EFA43B',
										cursor: 'pointer',
										fontSize: '1rem',
										fontWeight: 'bold',
										textDecoration: 'underline'
									}}
								>
									Înapoi la Autentificare
								</button>
							</div>
						</form>
					)}
				</div>
			)}

			{/* LOGIN or REGISTER */}
			{!isForgotPassword && (
				<div className={styles.card}>
					{/* LOGIN or REGISTER STEP 1: Email & Password */}
					{(!isRegistering || step === 1) && (
						<form onSubmit={isRegistering ? handleNextStep : handleSubmit}>
							<input
								type="email"
								placeholder="Adresă de Email *"
								className={styles.inputBase}
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								required
								disabled={loading}
							/>
							<input
								type="password"
								placeholder="Parolă *"
								className={styles.inputBase}
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								required
								disabled={loading}
								minLength={6}
							/>

							{!isRegistering && (
								<div style={{ textAlign: 'right', marginBottom: '0.75rem', marginTop: '-0.25rem' }}>
									<button
										type="button"
										onClick={() => { setIsForgotPassword(true); setError(''); }}
										style={{
											background: 'transparent',
											border: 'none',
											color: 'rgba(255,255,255,0.4)',
											cursor: 'pointer',
											fontSize: '0.85rem',
											textDecoration: 'underline',
											padding: 0
										}}
									>
										Ai uitat parola?
									</button>
								</div>
							)}

							{error && (
								<div className={`${styles.msgBox} ${styles.msgError}`}>
									{error}
								</div>
							)}

							<button type="submit" className={styles.button} disabled={loading} style={{ width: '100%' }}>
								{loading ? 'Se procesează...' : isRegistering ? 'Continuă' : 'Autentificare'}
							</button>

							<div style={{ marginTop: '1.5rem', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '1.5rem' }}>
								<p style={{ color: '#aaa', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
									{isRegistering ? 'Ești deja ambasador?' : "Nu ai încă un cont?"}
								</p>
								<button
									type="button"
									onClick={toggleMode}
									style={{
										background: 'transparent',
										border: 'none',
										color: '#EFA43B',
										cursor: 'pointer',
										fontSize: '1rem',
										fontWeight: 'bold',
										textDecoration: 'underline'
									}}
								>
									{isRegistering ? 'Autentifică-te aici' : 'Înregistrează-te ca Ambasador'}
								</button>
							</div>
						</form>
					)}

					{/* REGISTER STEP 2: Personal Details */}
					{isRegistering && step === 2 && (
						<form onSubmit={handleSubmit}>
							<div className={styles.formGrid}>
								<input
									type="text"
									placeholder="Prenume *"
									className={styles.inputBase}
									value={firstName}
									onChange={(e) => setFirstName(e.target.value)}
									required
									disabled={loading}
								/>
								<input
									type="text"
									placeholder="Nume *"
									className={styles.inputBase}
									value={lastName}
									onChange={(e) => setLastName(e.target.value)}
									required
									disabled={loading}
								/>
							</div>

							<input
								type="text"
								placeholder="Telefon *"
								className={styles.inputBase}
								value={telephone}
								onChange={(e) => setTelephone(e.target.value)}
								required
								disabled={loading}
							/>

							<div className={styles.formGrid}>
								<input
									type="text"
									placeholder="Instagram (@utilizator)"
									className={styles.inputBase}
									value={instagram}
									onChange={(e) => setInstagram(e.target.value)}
									disabled={loading}
								/>
								<input
									type="text"
									placeholder="TikTok (@utilizator)"
									className={styles.inputBase}
									value={tiktok}
									onChange={(e) => setTiktok(e.target.value)}
									disabled={loading}
								/>
							</div>

							{error && (
								<div className={`${styles.msgBox} ${styles.msgError}`}>
									{error}
								</div>
							)}

							<div style={{ display: 'flex', gap: '0.75rem' }}>
								<button
									type="button"
									onClick={() => { setStep(1); setError(''); }}
									className={styles.button}
									style={{ flex: 1, background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.6)' }}
									disabled={loading}
								>
									Înapoi
								</button>
								<button type="submit" className={styles.button} disabled={loading} style={{ flex: 2 }}>
									{loading ? 'Se creează contul...' : 'Înregistrare'}
								</button>
							</div>
						</form>
					)}
				</div>
			)}
		</div>
	);
};

export default Login;
